/**
 * Plantilla «Taller Demo» (paso 1.6, avance 5; propuesta aprobada el 2026-11-01, ADR 0013, 0017 y
 * 0021). Taller ficticio de la demo comercial: 10 elevadores (3 Fast Lane, 5 mixtos, 2 mayores),
 * 10 técnicos, 3 asesores, 1 controlador, 1 responsable de control de calidad, la bahía de espera
 * de control (6 lugares), 2 lavados, 3 cajones de recepción, 8 de patio y 10 de entrega, en un
 * terreno de 88 × 46 m (reglas y decisiones menores del 2026-11-04).
 *
 * La vía de acceso entra desde la calle (borde sur) hasta el ingreso: los autos que esperan para
 * entrar ocupan esa vía (3 autos) antes de quedar «en la calle» (pedido de Renato del 2026-11-02:
 * en un día normal no hay autos en la calle; en uno sobrecargado, sí).
 * Todos los valores son supuestos ilustrativos; la demanda queda registrada como supuesto (ADR 0021).
 */

import { CATALOGO } from '../catalogo/catalogo';
import type {
  Elemento,
  Espacio,
  Proyecto,
  TipoBahia,
  TipoVia,
  Via,
} from '../nucleo/dominio/espacio';
import { VERSION_ESQUEMA } from '../nucleo/dominio/espacio';
import { ESPACIO_DE_TRABAJO_PROPIO } from '../nucleo/dominio/organizacion';
import { supuesto } from '../nucleo/dominio/parametros';
import { modeloPosventa } from './modelo';
import { procesoTallerDemo } from './procesos';

type GeneradorId = () => string;

const NORTE = Math.PI;
const SUR = 0;

/**
 * Demanda calibrada del Taller Demo (autos por día; ADR 0021): con las reglas del 2026-11-04 y la
 * promesa en horas hábiles hasta las 17:00, el estado estable de 5 jornadas da 80.3 % de ocupación,
 * 64.6 % de cumplimiento y 8.2 % de trabajo pendiente del taller (100 réplicas). Queda como supuesto.
 */
export const DEMANDA_TALLER_DEMO = 33;

/**
 * Lugares de la bahía de espera de control (propuesta del avance 5): dentro del taller, entre el
 * último elevador y los lavados, mirando al pasillo como los elevadores.
 */
export const LUGARES_CONTROL = 6;

/** Elevadores de oeste a este: 2 mayores, 5 mixtos y 3 Fast Lane. */
const BAHIAS: TipoBahia[] = [
  'mayor',
  'mayor',
  'mixto',
  'mixto',
  'mixto',
  'mixto',
  'mixto',
  'fast-lane',
  'fast-lane',
  'fast-lane',
];
const Z_BAHIA = 7.8;
const Z_PASILLO = 14.2;
const Z_FILA = 19.7;
const Z_CALLE = 42;
/** El ingreso queda a 18.5 m del borde sur: caben 3 autos en la vía de acceso (uno cada 6 m). */
const Z_INGRESO = 27.5;
const X_OESTE = 4;

/**
 * Posiciones en x de la nave: los 10 elevadores, la bahía de control, los 2 lavados, la torre y el
 * almacén, en ese orden; la vía de acceso pasa frente al almacén. El terreno se ensancha lo que pida
 * la bahía de control (con 6 lugares, 88 × 46 m; la escena de referencia tenía 76 × 46 m).
 */
function nave(lugares: number) {
  const control = Array.from({ length: lugares }, (_, k) => 50.3 + 2.8 * k);
  const finControl = lugares > 0 ? control[lugares - 1]! + 1.3 : 48.45;
  const lavados = [finControl + 2.4, finControl + 7];
  const torre = lavados[1]! + 3.2;
  const almacen = torre + 5.6;
  const acceso = Math.round(almacen);
  const ancho = Math.ceil(almacen + 4 + 2.2);
  return { control, lavados, torre, almacen, acceso, ancho };
}

export function espacioTallerDemo(
  nuevoId: GeneradorId,
  nombre = 'Taller Demo',
  lugaresControl = LUGARES_CONTROL,
): Espacio {
  const n = nave(lugaresControl);
  const X_ACCESO = n.acceso;
  const el = (
    catalogoId: string,
    x: number,
    z: number,
    rotacion: number,
    extra: Partial<Elemento> = {},
  ): Elemento => ({
    id: nuevoId(),
    catalogoId,
    posicion: { x, z },
    rotacion,
    dimensiones: { ...CATALOGO[catalogoId]!.dimensionesPorDefecto },
    ...extra,
  });
  const via = (puntos: [number, number][], tipo: TipoVia = 'vehicular-doble'): Via => ({
    id: nuevoId(),
    tipo,
    puntos: puntos.map(([x, z]) => ({ x, z })),
    ancho: tipo === 'vehicular-doble' ? 6 : tipo === 'peatonal' ? 1.5 : 3.5,
  });
  const elementos: Elemento[] = [
    ...BAHIAS.map((tipoBahia, i) =>
      el('elevador', 7 + i * 4.4, Z_BAHIA, NORTE, { propiedades: { tipoBahia } }),
    ),
    ...n.control.map((x) => el('bahia-control', x, 8.05, NORTE)),
    ...n.lavados.map((x) => el('lavado', x, 7.4, NORTE)),
    el('torre-control', n.torre, 9.4, SUR),
    el('almacen-repuestos', n.almacen, 5.5, SUR, { etiqueta: 'Almacén' }),
    // Fila frente al pasillo: 8 de patio, 10 de entrega y 3 de recepción junto a la vía de acceso.
    ...Array.from({ length: 8 }, (_, i) => el('cajon-patio', 9 + i * 2.8, Z_FILA, SUR)),
    ...Array.from({ length: 10 }, (_, i) => el('cajon-entrega', 31.6 + i * 2.8, Z_FILA, SUR)),
    ...Array.from({ length: 3 }, (_, i) => el('cajon-recepcion', 59.6 + i * 2.8, Z_FILA, SUR)),
    el('oficina-asesores', 52.5, 29, SUR, { etiqueta: 'Asesores de servicio' }),
    el('acceso-peatonal', 52.5, 45.2, SUR),
    el('ingreso', X_ACCESO, Z_INGRESO, SUR),
    el('salida', 2.5, Z_CALLE, SUR),
  ];
  const vias: Via[] = [
    // Vía de acceso desde la calle (borde sur) hasta el pasillo.
    via([
      [X_ACCESO, 45.5],
      [X_ACCESO, Z_PASILLO],
    ]),
    // Pasillo frente a los elevadores, salida por el oeste y vía frente a la calle.
    via([
      [X_ACCESO, Z_PASILLO],
      [X_OESTE, Z_PASILLO],
    ]),
    via([
      [X_OESTE, Z_PASILLO],
      [X_OESTE, Z_CALLE],
    ]),
    via([
      [0.5, Z_CALLE],
      [X_ACCESO, Z_CALLE],
    ]),
    // Sendas peatonales: oficina ↔ acceso peatonal, almacén y torre ↔ pasillo.
    via(
      [
        [52.5, 32.5],
        [52.5, 45.2],
      ],
      'peatonal',
    ),
    via(
      [
        [n.almacen, 8],
        [n.almacen, Z_PASILLO],
      ],
      'peatonal',
    ),
    via(
      [
        [n.torre, 10.6],
        [n.torre, Z_PASILLO],
      ],
      'peatonal',
    ),
  ];
  const modelo = modeloPosventa(procesoTallerDemo());
  modelo.dotacion = {
    asesor: supuesto(3, 'personas'),
    tecnico: supuesto(10, 'personas'),
    calidad: supuesto(1, 'personas'),
    controlador: supuesto(1, 'personas'),
  };
  modelo.demanda = {
    ...modelo.demanda,
    unidadesPorDia: supuesto(DEMANDA_TALLER_DEMO, 'autos/día'),
  };
  // El asesor no promete para hoy lo que el corte del control (17:30) deja para mañana: las horas
  // hábiles de la promesa cuentan hasta las 17:00 (Renato, 2026-11-04; supuesto editable).
  modelo.atencion = {
    ...modelo.atencion,
    fin: supuesto('17:00', 'hh:mm', 'Media hora antes del corte del control de calidad (17:30).'),
  };
  modelo.refrigerio = {
    inicio: supuesto('13:00', 'hh:mm'),
    duracionMin: supuesto(60, 'min'),
    modo: supuesto('escalonado', ''),
  };
  return {
    id: nuevoId(),
    nombre,
    marco: { x: n.ancho / 2, z: 23, ancho: n.ancho, fondo: 46 },
    layout: { elementos, vias, pisos: [], muros: [], versionLayout: 1 },
    modelo,
    configuracionAleatoria: { semilla: 1, replicas: 20, replicasAutomaticas: true, dias: 1 },
  };
}

/** Proyecto «Taller Demo» con un espacio (la jornada de la demo; se verifica con 5 jornadas). */
export function crearTallerDemo(
  nuevoId: GeneradorId,
  ahora: string,
  nombre = 'Taller Demo',
): Proyecto {
  return {
    versionEsquema: VERSION_ESQUEMA,
    id: nuevoId(),
    espacioDeTrabajoId: ESPACIO_DE_TRABAJO_PROPIO,
    nombre,
    descripcion:
      'Plantilla ficticia de la demo comercial: 10 elevadores, 10 técnicos, 3 asesores, controlador y jefe de taller. Todos los valores son supuestos ilustrativos.',
    origen: 'plantilla:taller-demo',
    creadoEn: ahora,
    actualizadoEn: ahora,
    espacios: [espacioTallerDemo(nuevoId)],
  };
}
